import React from 'react';
import { useTranslation } from 'react-i18next';
import { murmurhash } from '../../../../../../../lib/js/hash';
import { TextMarker } from '../../../../../../../_lib/components/TextMarker/TextMarker';
import { Icons, SVG } from '../../../../../../atoms/SVG';
import { SearchBreadcrumb, SearchBreadcrumbModifier } from '../../../../../SearchBreadcrumb';
import { DownloadFileIcon } from '../../../../../../../_lib/components/Download';
import { Namespace } from '../../../../../../../translation/namespace';
import { PagePackState } from '../../../../../../../types/api/PagePackState';
import { AllContentResults, AllUserResult, TopContentResult } from '../types/SuggestResultTypes';
import { StateIndicator } from '../../../../../../../_lib/components/StateIndicator';
import { AttributeRules } from '../../../../../../../_lib/components/AttributeRules/AttributeRules';
import { AttributeRule } from '../../../../../../../types/api/AttributeGroups';

export interface ListItemPageProps {
  page: TopContentResult | AllContentResults | AllUserResult;
  link?: boolean;
  showOnlyFormatForFiles?: boolean;
}

/**
 * Item in the listbox that features content like linked site headlines.
 */
export default function ListItemPage({ page, link, showOnlyFormatForFiles }: ListItemPageProps) {
  const { t } = useTranslation(Namespace.FormsComboBox);

  if (page.type === 'allContentResult' || page.type === 'allUserResult') {
    return (
      <div className="combobox__list-page">
        <div
          id={murmurhash(page.pageLink.name)}
          className="combobox__list-item-padding combobox__list-item-padding--flex"
        >
          <span className="combobox__list-icon">
            <SVG svgSpriteId={Icons.ARROW_RIGHT} />
          </span>
          <TextMarker text={page.pageLink.name} type="strong" />
        </div>
      </div>
    );
  }

  const getPageStateIndicator = () => {
    return page.packState === PagePackState.ONLY_DRAFT_VERSION
      ? (
          <span className="combobox__draft-indicator">
            <StateIndicator status="red" />
          </span>
        )
      : null;
  };

  const getFileNameInfo = () => {
    const { format, parent, fileExtension } = page;

    let infoToShow = null;

    if (showOnlyFormatForFiles && fileExtension) {
      infoToShow = t('content.attachmentFormat', { format: fileExtension.toUpperCase() });
    } else if (format && parent) {
      infoToShow = t('content.attachmentOn', { format, page: parent.label });
    }

    if (!infoToShow) {
      return null;
    }

    return (
      <>
        {'\u200C\u0020'}
        {
          // please don't touch! we need whitespace here!
          ` (${infoToShow})`
        }
      </>
    );
  };

  const renderFileName = () => {
    return (
      <div className="combobox__filename">
        <TextMarker text={page.title} type="strong" />
        {getFileNameInfo()}
      </div>
    );
  };

  const renderFileIcon = () => {
    return (
      page.format &&
      page.parent &&
      page.fileExtension && <DownloadFileIcon modifier={['relative', 'small']} fileExtension={page.fileExtension} />
    );
  };

  const renderFileBreadcrumb = () => {
    return (
      page.breadcrumb && (
        <SearchBreadcrumb
          clickable={false}
          breadcrumbs={page.breadcrumb}
          modifier={SearchBreadcrumbModifier.NO_MARGIN_BOTTOM}
        />
      )
    );
  };

  const renderAttributeGroup = () => {
    const transformRules = (attributeRules: AttributeRule[]) =>
      attributeRules.map(rule => ({
        ...rule,
        attributeName: t(`PersonAttributes:${rule.attributeName}`),
      }));

    return (
      page.rules && (
        <AttributeRules
          rules={transformRules(page.rules)}
          ruleCombination={page.ruleCombination}
          modifier="combobox"
        />
      )
    );
  };

  if (link && page.pageLink && page.pageLink?.url) {
    return (
      <div className="combobox__list-item">
        {getPageStateIndicator()}
        {renderFileIcon()}
        {renderFileName()}
      </div>
    );
  }

  return (
    <div className="combobox__list-page">
      <div className="combobox__list-nolink">
        {getPageStateIndicator()}
        {renderFileIcon()}
        {renderFileName()}
      </div>
      {renderFileBreadcrumb()}
      {renderAttributeGroup()}
    </div>
  );
}
